/**
 * The pinpoint board's L0 strip: the four numbers that say whether anything on
 * this page is worth someone's afternoon.
 *
 * Three of them are the findings summarised — the waiting they cause, how many
 * there are, how much of the business they touch — and each opens the section
 * that explains it (§4.1: no dead aggregates). The fourth is the coverage tile,
 * and it is the one that keeps the other three honest.
 *
 * **Why coverage gets a whole tile here.** Every finding on this board is built
 * from F&O's own server-side timing, which covers the entire estate. Browser
 * telemetry — what the tab itself measured — covers whoever runs the managed
 * extension, and when this board was written that was 1 company of 31 and 17
 * form loads of 269,725. A board that showed verdicts without saying so would
 * invite the reader to think the fleet is instrumented. So the tile states both
 * ratios, and it links to `/experience`, which is the board that owns browser
 * coverage and the rollout that would change it.
 *
 * `coverage: null` means the panel did not measure it. That renders as "not
 * measured" with an empty track — never a zero, which would read as "no browser
 * reports at all" and is a different, stronger claim than "we did not ask".
 */

import { Statistic } from "@norican/react";

import { EXPERIENCE_PATH } from "@/features/experience/params";
import type { FoConsolePinpoint } from "@/lib/api";

import {
  formatExcessHours,
  lookbackOption,
  NOT_MEASURED,
  pinpointCompaniesAffected,
  pinpointCoverageLines,
  pinpointTotalExcessHours,
} from "./console-format";
import { KpiTile } from "./kpi-strip";
import { PINPOINT_COMPANIES_ANCHOR, PINPOINT_FINDINGS_ANCHOR } from "./params";
import { formatCount } from "./status";

interface PinpointKpiStripProps {
  pinpoint: FoConsolePinpoint;
  /** The findings the board is currently showing (after the company filter). */
  findings: FoConsolePinpoint["findings"];
}

export function PinpointKpiStrip({ pinpoint, findings }: PinpointKpiStripProps) {
  const windowCaption = lookbackOption(pinpoint.lookback).caption;
  const hours = pinpointTotalExcessHours(findings);
  const affected = pinpointCompaniesAffected(findings);
  const formAll = findings.filter((finding) => finding.company === "").length;
  const coverage = pinpointCoverageLines(pinpoint.coverage);

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <KpiTile href={`#${PINPOINT_FINDINGS_ANCHOR}`} tone={hours > 0 ? "warning" : "neutral"}>
        <Statistic
          label="Waiting caused"
          value={formatExcessHours(hours)}
          tone={hours > 0 ? "warning" : "neutral"}
          size="lg"
          caption={`Time people spent waiting above what the same form costs elsewhere, over the ${windowCaption}. Summed across every finding below.`}
        />
      </KpiTile>

      <KpiTile
        href={`#${PINPOINT_FINDINGS_ANCHOR}`}
        tone={findings.length > 0 ? "accent" : "neutral"}
      >
        <Statistic
          label="Findings"
          value={formatCount(findings.length)}
          size="lg"
          caption={
            findings.length === 0
              ? `Nothing cleared the backend's floors in the ${windowCaption}. An answer, not an empty page.`
              : `Ranked worst first, capped at 25 by the backend · ${windowCaption}`
          }
        />
      </KpiTile>

      <KpiTile href={`#${PINPOINT_COMPANIES_ANCHOR}`} tone="accent">
        <Statistic
          label="Companies affected"
          value={formatCount(affected)}
          size="lg"
          caption={`of ${formatCount(pinpoint.companies.length)} with F&O form loads in the ${windowCaption}${
            formAll > 0
              ? ` · ${formatCount(formAll)} findings are form-wide and name no single company`
              : ""
          }`}
        />
      </KpiTile>

      {/* One figure, like its three neighbours. It carried two meters and four
          label/value pairs — a small table wearing a tile's clothes, which
          stopped the row scanning as a row — and it printed the same two
          fractions the alert directly beneath it already spells out in prose.
          The alert keeps the detail; this keeps the headline. */}
      <KpiTile to={EXPERIENCE_PATH} tone="neutral">
        <Statistic
          label="Browser telemetry behind this"
          value={coverage.measured ? coverage.companiesPctText : NOT_MEASURED}
          size="lg"
          tone="neutral"
          caption={
            coverage.measured
              ? "of companies have a browser reporting. The findings do not depend on it — they rest on F&O's own server-side timing, which covers every company. Opens the rollout board."
              : "Coverage was not measured this time — unknown, not zero."
          }
        />
      </KpiTile>
    </div>
  );
}
